import React, { useEffect, useRef, useState } from "react";
import { Modal, ModalBody } from "reactstrap";
import { Building2, ChevronDown, Settings, ShieldCheck } from "lucide-react";
import { Button } from "@dhn/components/ui/button";
import { cn } from "@dhn/lib/utils";
import ItemDropDownConfiguration from "../configurationButton/ItemDropDownConfiguration";
import useConfigurationActions from "../configurationButton/use-actions";
import SecurityModal from "../modals/SecurityModal";
import noLogo from "../../../assets/img/brand/noLogo";
import HipaaCertificateButton from "@dhn/components/molecules/hipaa-certificate-button";
import UserNotifications from "@dhn/components/molecules/user-notifications";
import ModalHeader from "@dhn/components/molecules/modal-header";
import DoctorProfileModal from "../modals/DoctorProfileModal";
// Después de los componentes que cargan style.css para conservar la cascada.
import "./navbar-right.css";

const IMAGE_PREVIEW_INITIAL_STATE = {
  isOpen: false,
  src: "",
  title: "",
  alt: "",
};

function ImagePreviewModal({ preview, onClose }) {
  return (
    <Modal
      centered
      size="lg"
      isOpen={preview.isOpen}
      toggle={onClose}
      className="navbar-dhn__image-modal"
      contentClassName="navbar-dhn__image-modal-content"
      backdropClassName="navbar-dhn__image-modal-backdrop"
    >
      <ModalHeader
        toggle={onClose}
        iconClass="fa-image"
        description={preview.title}
        subtitle="Revise la imagen en tamaño completo."
      />
      <ModalBody className="navbar-dhn__image-modal-body">
        {preview.src && (
          <img
            src={preview.src}
            alt={preview.alt}
            className="navbar-dhn__image-modal-img"
          />
        )}
      </ModalBody>
    </Modal>
  );
}

export default function NavbarRight({
  user,
  dataDoctor,
  profileName,
  permissionViews,
  branchOfficeOption,
  currentBranchName,
  selectedBranchId,
  onChangeBranch,
  logo,
  onSignOut,
  // Personalización para farmacia y call center; consultorio usa los valores por defecto.
  centerName: centerNameProp,
  logoAlt = "logo farmacia",
  userCardLabel = "Ver información del médico",
  configItems,
  configSubtitle,
  renderProfileModal,
  // Administración ya muestra el logo de la empresa a la izquierda del header.
  showCenterLogo = true,
  // Administración oculta las notificaciones por ahora.
  showNotifications = true,
}) {
  const [imagePreview, setImagePreview] = useState(IMAGE_PREVIEW_INITIAL_STATE);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [isDoctorProfileOpen, setIsDoctorProfileOpen] = useState(false);
  const configRef = useRef(null);
  const [{ state: securityState, bodyItem }, securityActions] =
    useConfigurationActions();

  useEffect(() => {
    if (!isConfigOpen) return undefined;

    const handlePointerDown = (event) => {
      if (event.target.closest(".dhn-ui-dialog-content, .modal")) {
        return;
      }

      if (!configRef.current?.contains(event.target)) {
        setIsConfigOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isConfigOpen]);

  const openImagePreview = ({ src, title, alt }) => {
    setImagePreview({
      isOpen: true,
      src,
      title,
      alt,
    });
  };

  const closeImagePreview = () => {
    setImagePreview(IMAGE_PREVIEW_INITIAL_STATE);
  };

  const centerLogo = logo || noLogo;
  const centerName =
    centerNameProp || dataDoctor?.centerName || "Logo del centro";
  const userInitials =
    `${user?.firstName?.[0] || ""}${user?.lastName?.[0] || ""}`.toUpperCase() ||
    "DR";

  return (
    <>
      <div className="doctor-office-navbar__right navbar-dhn__right">
        <div className="doctor-office-command-center">
          <div className="doctor-office-trust-panel">
            {showCenterLogo && (
              <button
                type="button"
                className="doctor-office-center-logo"
                onClick={() =>
                  openImagePreview({
                    src: centerLogo,
                    title: centerName,
                    alt: centerName,
                  })
                }
                aria-label="Ver logo del centro"
              >
                <img
                  src={centerLogo}
                  className="doctor-office-center-logo__img"
                  alt={logoAlt}
                />
              </button>
            )}

            <div className="doctor-office-navbar__hipaa">
              <HipaaCertificateButton width={86} height={42} />
            </div>
          </div>

          <div className="doctor-office-user-card">
            <button
              type="button"
              className="doctor-office-user-card__inner doctor-office-user-card__trigger"
              onClick={() => setIsDoctorProfileOpen(true)}
              title={userCardLabel}
              aria-label={userCardLabel}
            >
              <span className="doctor-office-user-card__avatar">
                {userInitials}
                <span className="doctor-office-user-card__status" />
              </span>

              <div className="doctor-office-user-card__body">
                <div className="doctor-office-user-card__name">
                  {`${user.firstName} ${user.lastName}`}
                </div>

                <div className="doctor-office-user-card__center">
                  <Building2 size={14} />
                  <span>{centerNameProp || dataDoctor?.centerName}</span>
                </div>

                {profileName && (
                  <div
                    className="doctor-office-user-card__center doctor-office-user-card__profile"
                    title={`Perfil: ${profileName}`}
                  >
                    <ShieldCheck size={14} />
                    <span>{profileName}</span>
                  </div>
                )}
              </div>
            </button>
          </div>

          <div className="doctor-office-actions">
            <div className="doctor-office-actions__buttons">
              {showNotifications && <UserNotifications />}
              <div className="doctor-office-actions__config" ref={configRef}>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={cn(
                    "doctor-office-actions__config-button",
                    isConfigOpen && "is-open",
                  )}
                  onClick={() => setIsConfigOpen((value) => !value)}
                  aria-expanded={isConfigOpen}
                  aria-controls="doctor-office-configuration-panel"
                  aria-haspopup="dialog"
                  title="Configuración"
                  aria-label="Configuración"
                >
                  <Settings size={17} />
                  <span className="doctor-office-actions__config-label">
                    Configuración
                  </span>
                  <ChevronDown
                    size={16}
                    className={cn(
                      "doctor-office-actions__chevron",
                      isConfigOpen && "is-open",
                    )}
                  />
                </Button>

                {isConfigOpen && (
                  <ItemDropDownConfiguration
                    permissionViews={permissionViews}
                    dataDoctor={dataDoctor}
                    branchOfficeOption={branchOfficeOption}
                    currentBranchName={currentBranchName}
                    selectedBranchId={selectedBranchId}
                    onChangeBranch={onChangeBranch}
                    onSignOut={onSignOut}
                    onNavigate={() => setIsConfigOpen(false)}
                    onSelectItem={securityActions.onChange}
                    items={configItems}
                    defaultItems={bodyItem}
                    subtitle={configSubtitle}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ImagePreviewModal preview={imagePreview} onClose={closeImagePreview} />
      {/* Fuera del menú de configuración para poder cerrarlo al abrir el modal. */}
      <SecurityModal
        state={securityState}
        isOpen={securityState.isOpenModalSecurity}
        onChange={securityActions.onChange}
        toggle={securityActions.toggle}
        onModifiyPassword={securityActions.onModifiyPassword}
        username={user?.username}
      />
      {renderProfileModal ? (
        renderProfileModal({
          isOpen: isDoctorProfileOpen,
          onClose: () => setIsDoctorProfileOpen(false),
        })
      ) : (
        <DoctorProfileModal
          isOpen={isDoctorProfileOpen}
          onClose={() => setIsDoctorProfileOpen(false)}
          dataDoctor={dataDoctor}
          user={user}
        />
      )}
    </>
  );
}
